import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useStudentDetail, useStudentProgress } from '../hooks';
import { createStudentNote, getStudentNotes } from '../services/studentsApi';
import { getSubmissions } from '../services/gradingApi';
import { parseApiError } from '../services/apiClient';
import { useConversations, useMessagesWebSocket } from '../hooks/useMessages';
import ConversationThread from '../components/ConversationThread';
import styles from './StudentDetail.module.css';

type TabKey = 'progress' | 'submissions' | 'notes' | 'messages';
const TABS: TabKey[] = ['progress', 'submissions', 'notes', 'messages'];

function dateLabel(iso?: string, relative = false): string {
  if (!iso || !Number.isFinite(new Date(iso).getTime())) return '—';
  return relative
    ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true })
    : format(new Date(iso), 'MMM d, yyyy');
}

const PrivateNotes: React.FC<{ studentId: string }> = ({ studentId }) => {
  const [draft, setDraft] = useState('');
  const queryClient = useQueryClient();
  const key = ['students', 'notes', studentId];
  const notes = useQuery({
    queryKey: key,
    queryFn: () => getStudentNotes(studentId),
  });
  const save = useMutation({
    mutationFn: () => createStudentNote(studentId, draft),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  return (
    <section className={styles.card} aria-label="Private instructor notes">
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Private notes</h2>
      </header>
      <form
        className={styles.noteForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (draft.trim()) save.mutate();
        }}
      >
        <p>
          Visible only to you. Record teaching context and follow-up actions
          without including secrets or unnecessary personal information.
        </p>
        <label htmlFor="student-note">Add a note</label>
        <textarea
          id="student-note"
          rows={4}
          maxLength={4000}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={save.isPending}
        />
        <div className={styles.noteActions}>
          <span>{draft.length} / 4000</span>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={!draft.trim() || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save note'}
          </button>
        </div>
        {save.isError && (
          <p role="alert">{parseApiError(save.error).message}</p>
        )}
        {save.isSuccess && <p role="status">Note saved.</p>}
      </form>
      {notes.isLoading ? (
        <p className={styles.empty} role="status">
          Loading notes…
        </p>
      ) : notes.isError ? (
        <div className={styles.empty} role="alert">
          <p>Couldn&apos;t load your notes.</p>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => notes.refetch()}
          >
            Retry
          </button>
        </div>
      ) : notes.data?.length ? (
        <ul className={styles.notesList}>
          {notes.data.map((note) => (
            <li key={note.id}>
              <time dateTime={note.createdAt}>
                {dateLabel(note.createdAt, true)}
              </time>
              <p>{note.body}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.empty}>No private notes yet.</p>
      )}
    </section>
  );
};

const StudentMessages: React.FC<{ studentId: string }> = ({ studentId }) => {
  useMessagesWebSocket();
  const conversations = useConversations({ studentId });
  const conversation = conversations.data?.find(
    (item) => item.studentId === studentId,
  );
  if (conversations.isLoading)
    return (
      <p className={styles.empty} role="status">
        Loading conversation…
      </p>
    );
  if (conversations.isError)
    return (
      <div className={styles.empty} role="alert">
        <p>Couldn&apos;t load messages.</p>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => conversations.refetch()}
        >
          Retry
        </button>
      </div>
    );
  if (conversation)
    return (
      <div className={styles.thread}>
        <ConversationThread conversationId={conversation.id} />
      </div>
    );
  return (
    <section className={styles.card}>
      <div className={styles.empty}>
        <p>No conversation yet.</p>
        <Link
          to={`/messages?studentId=${encodeURIComponent(studentId)}`}
          className={styles.btnPrimary}
        >
          Start a conversation
        </Link>
      </div>
    </section>
  );
};

const StudentSubmissions: React.FC<{ studentId: string }> = ({ studentId }) => {
  const [page, setPage] = useState(1);
  const submissions = useQuery({
    queryKey: ['grading', 'student', studentId, page],
    queryFn: () => getSubmissions({ userId: studentId, page, limit: 20 }),
  });
  return (
    <section className={styles.card} aria-label="Student submissions">
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Submissions</h2>
        <Link
          to={`/grading?userId=${encodeURIComponent(studentId)}`}
          className={styles.btnSecondary}
        >
          Review work
        </Link>
      </header>
      {submissions.isLoading ? (
        <p className={styles.empty} role="status">
          Loading submissions…
        </p>
      ) : submissions.isError ? (
        <div className={styles.empty} role="alert">
          <p>Couldn&apos;t load submissions.</p>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => submissions.refetch()}
          >
            Retry
          </button>
        </div>
      ) : !submissions.data?.data.length ? (
        <p className={styles.empty}>No submissions recorded.</p>
      ) : (
        <>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Lab</th>
                  <th scope="col">Status</th>
                  <th scope="col">Grade</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Review</th>
                </tr>
              </thead>
              <tbody>
                {submissions.data.data.map((submission) => (
                  <tr key={submission.id}>
                    <td>{submission.labTitle}</td>
                    <td>{submission.status}</td>
                    <td>
                      {submission.finalScore ?? submission.manualScore ?? '—'}
                    </td>
                    <td>{dateLabel(submission.submittedAt, true)}</td>
                    <td>
                      <Link
                        to={`/grading?userId=${encodeURIComponent(studentId)}&status=${submission.status === 'graded' ? 'graded' : 'pending'}&submissionId=${encodeURIComponent(submission.id)}`}
                      >
                        Open submission
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className={styles.noteActions} aria-label="Submission pages">
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span>
              Page {page} of {submissions.data.pagination.totalPages}
            </span>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setPage(page + 1)}
              disabled={page >= submissions.data.pagination.totalPages}
            >
              Next
            </button>
          </nav>
        </>
      )}
    </section>
  );
};

const StudentDetail: React.FC = () => {
  const { studentId = '' } = useParams<{ studentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  // The URL is the tab authority, including Back/Forward, deep links, and
  // same-component student changes. Never retain a prior student's tab state.
  const tab: TabKey = TABS.includes(location.hash.slice(1) as TabKey)
    ? (location.hash.slice(1) as TabKey)
    : 'progress';
  const setTab = (key: TabKey) => navigate(
    { pathname: location.pathname, search: location.search, hash: `#${key}` },
    { replace: true },
  );
  const {
    data: student,
    isLoading,
    isError,
    refetch,
  } = useStudentDetail(studentId);
  const labs = useStudentProgress(studentId);
  if (isError)
    return (
      <div className={styles.page}>
        <div className={styles.loading} role="alert">
          <p>
            Couldn&apos;t load this student. Your account may not have access.
          </p>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => refetch()}
          >
            Retry
          </button>
          <Link to="/students">Back to students</Link>
        </div>
      </div>
    );
  if (isLoading || !student)
    return (
      <div className={styles.loading} role="status">
        Loading student…
      </div>
    );
  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/students" className={styles.breadcrumbLink}>
          Students
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">
          {student.firstName} {student.lastName}
        </span>
      </nav>
      <section className={styles.masthead}>
        <div className={styles.avatarLg} aria-hidden="true">
          {`${student.firstName?.[0] ?? ''}${student.lastName?.[0] ?? ''}`.toUpperCase() ||
            'ST'}
        </div>
        <div className={styles.mastheadCopy}>
          <h1 className={styles.name}>
            {student.firstName} {student.lastName}
          </h1>
          <p className={styles.meta}>
            {student.email} · Joined {dateLabel(student.createdAt)}
          </p>
          <p className={styles.meta}>
            Last active {dateLabel(student.lastActiveAt, true)} ·{' '}
            {student.status}
          </p>
        </div>
        <div className={styles.mastheadActions}>
          <Link
            className={styles.btnSecondary}
            to={`/messages?studentId=${encodeURIComponent(studentId)}`}
          >
            Message
          </Link>
          <Link
            className={styles.btnPrimary}
            to={`/grading?userId=${encodeURIComponent(studentId)}`}
          >
            Grade work
          </Link>
        </div>
      </section>
      <div
        className={styles.tabsBar}
        role="tablist"
        aria-label="Student sections"
      >
        {TABS.map((key, index) => (
          <button
            key={key}
            type="button"
            id={`student-tab-${key}`}
            role="tab"
            aria-selected={tab === key}
            aria-controls="student-panel"
            tabIndex={tab === key ? 0 : -1}
            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
            onClick={() => setTab(key)}
            onKeyDown={(event) => {
              let next = index;
              if (event.key === 'ArrowRight') next = (index + 1) % TABS.length;
              else if (event.key === 'ArrowLeft')
                next = (index + TABS.length - 1) % TABS.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = TABS.length - 1;
              else return;
              event.preventDefault();
              setTab(TABS[next]);
              document.getElementById(`student-tab-${TABS[next]}`)?.focus();
            }}
          >
            {key[0].toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>
      <div
        id="student-panel"
        role="tabpanel"
        aria-labelledby={`student-tab-${tab}`}
        className={styles.panel}
      >
        {tab === 'progress' && (
          <>
            <section
              className={styles.summary}
              aria-label="Recorded lab progress"
            >
              <div>
                <strong>{student.labsCompleted}</strong>
                <span>Labs completed</span>
              </div>
              <div>
                <strong>{student.labsInProgress}</strong>
                <span>Labs in progress</span>
              </div>
              <div>
                <strong>
                  {student.averageScore != null
                    ? `${student.averageScore}%`
                    : '—'}
                </strong>
                <span>Average recorded score</span>
              </div>
            </section>
            <section className={styles.card}>
              <header className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Lab activity</h2>
              </header>
              {labs.isLoading ? (
                <p className={styles.empty} role="status">
                  Loading lab activity…
                </p>
              ) : labs.isError ? (
                <div className={styles.empty} role="alert">
                  <p>Couldn&apos;t load lab activity.</p>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => labs.refetch()}
                  >
                    Retry
                  </button>
                </div>
              ) : !labs.data?.length ? (
                <p className={styles.empty}>No lab activity recorded.</p>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Lab</th>
                        <th scope="col">Status</th>
                        <th scope="col">Score</th>
                        <th scope="col">Last activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labs.data.map((lab) => (
                        <tr key={lab.labId}>
                          <td>{lab.labTitle}</td>
                          <td>{lab.status.replace(/_/g, ' ')}</td>
                          <td>{lab.score == null ? '—' : `${lab.score}%`}</td>
                          <td>{dateLabel(lab.lastActivityAt, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
        {tab === 'submissions' && (
          <StudentSubmissions key={studentId} studentId={studentId} />
        )}
        {tab === 'notes' && (
          <PrivateNotes key={studentId} studentId={studentId} />
        )}
        {tab === 'messages' && (
          <StudentMessages key={studentId} studentId={studentId} />
        )}
      </div>
    </div>
  );
};

export default StudentDetail;
